<template>
  <IonPage>
    <MapModalHeader>
      <RepeaterToolbar></RepeaterToolbar>
    </MapModalHeader>
    <IonContent color="white">
      <IonRefresher :mode="refresherMode" slot="fixed" @ion-refresh="IonRefresh($event)">
        <IonRefresherContent :mode="refresherMode"></IonRefresherContent>
      </IonRefresher>
      <div class="p-6">
        <Description
          :text="description.text"
          :created-at="description.createdAt"
          :created-by="description.createdBy"
          :on-save="saveDescription"
        />

        <!-- Repeater Img Section -->
        <div class="flex items-center justify-center text-lg text-gray-700 font-medium mt-4">
          <i class="icon-barcode mr-2 text-gray-500"></i>
          <span>{{ repeaterIdStr }}</span>
        </div>
        <DeviceImg class="mx-auto -my-4 w-48" :device="repeater" />
        <DeviceOnlineStatus class="mb-10" :device="repeater" />
        <IonList inset>
          <IonItem class="repeater-action-item" color="whitesmoke" lines="full">
            <div class="w-full flex justify-evenly items-center text-sm">
              <BaseClearButton @click="changeClicked()" color="light" size="small" class="text-nowrap"
                ><i class="icon-barcode text-blue-500 mr-1"></i>Change</BaseClearButton
              >
              <PingPoweredDevice
                v-if="selectedProperty.property?.gateway?.id && repeater?.id"
                :gateway-id="selectedProperty.property.gateway.id"
                :device-id="repeater.id"
                @on-ping="onPingSuccess()"
              ></PingPoweredDevice>
            </div>
          </IonItem>
        </IonList>
        <HopsAway ref="hopsAway" class="mb-6" v-if="repeater?.id" :device-id="repeater?.id"></HopsAway>
        <IonList inset>
          <IonItem class="repeater-action-item" color="whitesmoke" lines="full">
            <div class="w-full flex justify-between px-4 items-center text-sm">
              <div class="flex items-center">
                <i class="icon-waves text-gray-500 mr-1"></i>
                <span class="text-gray-700 font-medium mr-0.5">{{ signalStrengthParts[0] }}</span>
                <span class="text-gray-500 font-medium">{{ signalStrengthParts[1] }}</span>
              </div>
              <div class="flex items-center">
                <i class="icon-transceiver text-gray-500 mr-1"></i>
                <span class="text-gray-700 font-medium">
                  {{ childrenCount }}
                </span>
              </div>
              <div class="flex items-center">
                <i class="icon-thermometer text-gray-500 mr-1"></i>
                <span class="text-gray-700 font-medium mr-0.5">{{ temperatureParts[0] }}</span>
                <span class="text-gray-500 font-medium">{{ temperatureParts[1] }}</span>
              </div>
            </div>
          </IonItem>
        </IonList>

        <RepeaterSignalStrength :property-id="selectedProperty.propertyId" :repeater-id="repeater?.id" class="mt-12" />
        <AdditionalInfo
          class="py-12"
          :photo="photo"
          :allow-photos="true"
          context="REPEATER"
          :on-upload-photo="uploadPhoto"
          :comments="repeater?.comments"
          :on-refresh="IonRefresh"
          :on-add-comment="addComment"
          :on-remove-comment="removeComment"
        />

        <SectionTitle>Quick Actions</SectionTitle>
        <QuickActionButton
          icon="icon-map-pin"
          title="Drop a Pin"
          subtitle="Save your current location as this Repeater's location"
          @click="dropPin()"
        />
        <ReplaceRepeaterAction
          ref="replaceRepeaterAction"
          v-if="repeater?.id"
          :repeater-id="repeater.id"
        ></ReplaceRepeaterAction>
        <QuickActionButton
          color="red"
          icon="icon-trash"
          :title="isNextLink ? 'Remove NextLink' : 'Remove Repeater'"
          :subtitle="`Delete ${repeaterIdStr} from ${selectedProperty.property?.name}`"
          @click="removeRepeater()"
        />
      </div>
    </IonContent>
  </IonPage>
</template>

<script setup lang="ts">
import { useSelectedProperty } from '@/stores/selected-property.store';
import {
  actionSheetController,
  alertController,
  createAnimation,
  IonContent,
  IonItem,
  IonList,
  IonPage,
  IonRefresher,
  IonRefresherContent,
  loadingController,
  onIonViewWillEnter,
  onIonViewWillLeave,
  useIonRouter,
} from '@ionic/vue';
import { ConversionUtils, Lorax } from '@ncss/models';
import { Repeater } from '@ncss/models/dist/v3';
import { computed, ref, useTemplateRef, watchEffect } from 'vue';
import { useRoute } from 'vue-router';
import DeviceImg from '../components/DeviceImg.vue';
import DeviceOnlineStatus from '../components/DeviceOnlineStatus.vue';
import Description from '../components/Description.vue';
import RepeaterSignalStrength from './RepeaterSignalStrength.vue';
import AdditionalInfo from '../components/AdditionalInfo.vue';
import SectionTitle from '@/components/forms/SectionTitle.vue';
import QuickActionButton from '@/components/forms/QuickActionButton.vue';
import { useToast } from '@/composables/use-toast';
import { getUrlForPhoto } from '@/components/photos/photoUtils';
import { useLocation } from '@/stores/location.store';
import ReplaceRepeaterAction from './ReplaceRepeaterAction.vue';
import PingPoweredDevice from '@/components/devices/PingPoweredDevice.vue';
import HopsAway from '@/components/devices/HopsAway.vue';
import BaseClearButton from '@/components/forms/BaseClearButton.vue';
import { useRefresherMode } from '@/composables/use-refresher-mode';
import { NextLink } from '@ncss/models/dist/v3';
import RepeaterToolbar from '../toolbars/RepeaterToolbar.vue';
import MapModalHeader from '../MapModalHeader.vue';

const { successToast, errorToast } = useToast();
const selectedProperty = useSelectedProperty();
const router = useIonRouter();
const repeater = ref<Repeater | NextLink | undefined>();
const repeaterIdStr = computed(() => {
  return repeater.value?.id ? ConversionUtils.ConvertSerialNumberToString(repeater.value.id) : '';
});
const isNextLink = computed(() => repeater.value instanceof NextLink);
const location = useLocation();
const route = useRoute();
const hopsAway = useTemplateRef('hopsAway');
const childrenCount = ref(0);
watchEffect(() => (childrenCount.value = hopsAway.value?.state?.childrenCount || 0));
const replaceRepeaterAction = useTemplateRef('replaceRepeaterAction');
const description = computed(() => {
  return {
    text: repeater.value?.description,
    createdAt: repeater.value?.descriptionChangedOn,
    createdBy: repeater.value?.descriptionChangedBy,
  };
});

const { mode: refresherMode } = useRefresherMode();
const photo = computed(() => {
  const latestPicture = repeater.value?.docs?.findLast((d) => /image/.test(d.type));
  if (!latestPicture || !selectedProperty.property?._id) {
    return;
  }
  return {
    ...latestPicture,
    url: latestPicture.url || getUrlForPhoto(selectedProperty.property?._id, latestPicture.name),
  };
});

const signalStrengthParts = computed(() => {
  if (repeater.value?.data) {
    const l = Lorax.getLinkQualityFromRead(repeater.value?.data as any);
    return [l.result.percent.replace('%', ''), '%'];
  }
  return ['-', '%'];
});

const temperatureParts = computed(() => {
  if (repeater.value?.data) {
    const l = Lorax.getTemperatureFromRead(repeater.value?.data as any);
    return [l.result.replace(/C|F/gi, ''), l.result.replace(/\d+|\./gi, '')];
  }
  return ['-', Lorax.useMetric ? 'C' : 'F'];
});

async function IonRefresh(e?: CustomEvent) {
  await selectedProperty.refresh();
  (e?.target as any)?.complete();
}

function onPingSuccess() {
  setTimeout(() => {
    IonRefresh();
  }, 800);
}

watchEffect(() => {
  const id = +route.params.repeaterId;
  repeater.value = selectedProperty.property?.repeaters?.find((r) => r.id === id) || undefined;
});

async function uploadPhoto(base64?: string) {
  if (!base64 || !repeater.value?.id) {
    return;
  }
  await selectedProperty.addRepeaterPhoto(repeater.value.id, base64);
  successToast('Photo saved!');
}

async function addComment(text: string) {
  if (!repeater.value?.id) {
    return;
  }
  await selectedProperty.addRepeaterComment(repeater.value.id, text);
}

async function removeComment(commentId: string) {
  if (!repeater.value?.id) {
    return;
  }
  await selectedProperty.removeRepeaterComment(repeater.value.id, commentId);
}

async function saveDescription(description: string) {
  if (!repeater.value?.id) {
    return { success: false };
  }
  await selectedProperty.updateRepeaterDescription(repeater.value.id, description);
  return { success: true };
}

async function dropPin() {
  const repeaterId = repeater.value?.id;
  if (repeaterId) {
    const l = await loadingController.create({
      message: 'Saving location...',
    });
    await l.present();
    const coords = await location.getCoordinates();
    if (coords) {
      const { success, error } = await selectedProperty.saveRepeaterLocation(repeaterId, {
        lat: coords[1],
        long: coords[0],
      });
      await l.dismiss();
      if (success) {
        successToast('Repeater location saved!');
      } else if (error) {
        errorToast(error);
      }
    } else {
      await l.dismiss();
      errorToast('Unable to retrieve current location');
    }
  }
}

async function removeRepeater() {
  const repeaterId = repeater.value?.id;
  if (repeaterId) {
    const repeaterIdStr = repeaterId.toString(16).toUpperCase();
    const a = await alertController.create({
      header: 'Are You Sure?',
      message: `Are you sure you want to remove ${repeaterIdStr} from this property?`,
      buttons: [
        {
          role: 'cancel',
          text: 'Cancel',
        },
        {
          role: 'destructive',
          text: `Remove ${repeaterIdStr}`,
        },
      ],
    });
    await a.present();
    const { role } = await a.onDidDismiss();
    if (role !== 'destructive') {
      return;
    }
    const { success, error } = await selectedProperty.removeRepeater(repeaterId);
    if (success) {
      successToast('Repeater removed!');
      if (selectedProperty.property) {
        router.replace(`/network/${selectedProperty.property._id}`, () => createAnimation());
      }
    } else if (error) {
      errorToast(error);
    }
  }
}

async function changeClicked() {
  const a = await actionSheetController.create({
    buttons: [
      { role: 'cancel', text: 'Cancel' },
      { role: 'replace', text: 'Replace Repeater' },
      { role: 'destructive', text: 'Remove Repeater' },
    ],
  });
  await a.present();
  const { role } = await a.onDidDismiss();
  if (role === 'destructive') {
    removeRepeater();
  } else if (role === 'replace') {
    replaceRepeaterAction.value?.present();
  }
}
</script>

<style scoped>
/* For some reason Android needs this  */
ion-refresher {
  z-index: 10;
}

ion-item.repeater-action-item::part(native) {
  --border-width: 1px;
}
ion-item.repeater-action-item {
  --padding-start: 8px;
  --padding-end: 8px;
  --inner-padding-end: 0px;
  --inner-padding-start: 0px;
}
</style>
